// Title, description and link-preview tags of the page being shown.
//
// The build already writes them into each public page's own HTML
// (scripts/build-seo-pages.js). This keeps them right while the visitor moves
// around inside the app, where no new HTML is loaded. Same page list for
// both: seoPages.json.
//
// Public pages exist in every website language (lib/siteLanguages.js) unless
// they list their own `langs` (the legal texts: Spanish only). Their Spanish
// address has no prefix (/funcionalidades); the others do (/fr/funcionalidades).
// Pages marked `app` (sign-in, licence) follow the APP's language instead.
import cfg from './seoPages.json';
import { CODES, DEFAULT_LANGUAGE, languageInfo, localizedPath, splitLocalePath } from './siteLanguages';

const normalize = (pathname) => {
  const p = String(pathname || '/').split(/[?#]/)[0].replace(/\/+$/, '');
  return p === '' ? '/' : p;
};

// Languages a public page exists in.
export function pageLanguages(page) {
  return (page && page.langs) || CODES;
}

export function pageAvailableIn(page, lang) {
  return pageLanguages(page).includes(lang);
}

// The page at an address — '/funcionalidades', its alias '/verifactu', or a
// language version '/fr/funcionalidades' — or null. A language version exists
// only for the page's own address and only in the page's languages; '/es/x'
// is the Spanish page x.
export function findPage(pathname) {
  const { lang, path } = splitLocalePath(normalize(pathname));
  if (lang) {
    const page = cfg.pages.find((p) => p.path === path) || null;
    if (!page || page.app) return null;
    return lang === DEFAULT_LANGUAGE || pageAvailableIn(page, lang) ? page : null;
  }
  return cfg.pages.find((p) => p.path === path || (p.aliases || []).includes(path)) || null;
}

// Language of the public page at an address: from its prefix, else Spanish.
export function pageLanguageAt(pathname) {
  return splitLocalePath(normalize(pathname)).lang || DEFAULT_LANGUAGE;
}

const urlOf = (path) => `${cfg.site}${path}`;

// <link rel="alternate" hreflang> of a page: one per language it exists in,
// plus x-default (the Spanish address, where "/" picks the visitor's language).
export function alternatesFor(page) {
  const langs = pageLanguages(page);
  if (!page || page.app || langs.length < 2) return [];
  return [
    ...langs.map((l) => ({ hreflang: l, href: urlOf(localizedPath(page.path, l)) })),
    { hreflang: 'x-default', href: urlOf(page.path) },
  ];
}

// What the head must say at this address, or null when the page writes its
// own (public catalogues name themselves after the catalogue).
//   appLang  the app's language (for the `app` pages)
//   seoOf    (lang, path) → { title, description } of a translated page
export function metaFor(pathname, appLang, seoOf = () => undefined) {
  const path = normalize(pathname);
  if (cfg.selfManaged.some((prefix) => `${path}/`.startsWith(prefix))) return null;
  const page = findPage(path);
  if (!page) return { ...cfg.default, canonical: null, alternates: [], lang: null };
  if (page.app) {
    const words = page.i18n?.[String(appLang || 'es').slice(0, 2)] || page;
    return { title: words.title, description: words.description, canonical: urlOf(page.path), alternates: [], lang: null };
  }
  const lang = pageLanguageAt(path);
  const words = (lang !== DEFAULT_LANGUAGE && seoOf(lang, page.path)) || page;
  return {
    title: words.title,
    description: words.description,
    // /es/x is the Spanish page: its canonical address is x.
    canonical: urlOf(localizedPath(page.path, lang)),
    alternates: alternatesFor(page),
    lang,
    ogLocale: languageInfo(lang).og,
  };
}

const upsert = (doc, selector, create, attr, value) => {
  let el = doc.head.querySelector(selector);
  if (value === null || value === undefined) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = create();
    doc.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const metaTag = (doc, key, name) => () => {
  const el = doc.createElement('meta');
  el.setAttribute(key, name);
  return el;
};

export function applyMeta(doc, meta) {
  if (!meta || !doc?.head) return;
  doc.title = meta.title;
  upsert(doc, 'meta[name="description"]', metaTag(doc, 'name', 'description'), 'content', meta.description);
  upsert(doc, 'link[rel="canonical"]', () => {
    const el = doc.createElement('link');
    el.setAttribute('rel', 'canonical');
    return el;
  }, 'href', meta.canonical);
  upsert(doc, 'meta[property="og:title"]', metaTag(doc, 'property', 'og:title'), 'content', meta.title);
  upsert(doc, 'meta[property="og:description"]', metaTag(doc, 'property', 'og:description'), 'content', meta.description);
  upsert(doc, 'meta[property="og:url"]', metaTag(doc, 'property', 'og:url'), 'content', meta.canonical);
  upsert(doc, 'meta[name="twitter:title"]', metaTag(doc, 'name', 'twitter:title'), 'content', meta.title);
  upsert(doc, 'meta[name="twitter:description"]', metaTag(doc, 'name', 'twitter:description'), 'content', meta.description);
  if (meta.ogLocale) upsert(doc, 'meta[property="og:locale"]', metaTag(doc, 'property', 'og:locale'), 'content', meta.ogLocale);
  // The language versions of this page (none outside the public website).
  doc.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
  for (const a of meta.alternates || []) {
    const el = doc.createElement('link');
    el.setAttribute('rel', 'alternate');
    el.setAttribute('hreflang', a.hreflang);
    el.setAttribute('href', a.href);
    doc.head.appendChild(el);
  }
}
